import type { Chat, PublicUser, Space, SpaceRole, Workflow } from "@rhema/client";
import type { MenuItem } from "@rhema/ui";
import { useNavigate } from "react-router";
import { useIsDesktop } from "../app/media";
import { chatTitle, firstName, isUnread, peerOf } from "../data/view";
import { contactsChanged } from "../people/useContacts";
import { useQueravelPanel } from "../queravel/panel";
import { useApi, useClient, useMe, useSession } from "../session/session";
import {
  channelsOf,
  isForeign,
  isGuest,
  isAdmin as isSpaceAdmin,
  spaceActivity,
  spaceHref,
  spaceInviteLink,
} from "../spaces/data";
import { copyText } from "./clipboard";
import { leaveGroupAsk } from "./confirms";
import { useFeedback } from "./Feedback";
import {
  isMutedUntil,
  MUTE_CHOICES,
  type MuteChoice,
  mutedForever,
  mutedNote,
  muteEnd,
  muteEndLabel,
} from "./mute";

type Item = Extract<MenuItem, { onSelect: () => void }>;
const item = (
  id: string,
  label: string,
  onSelect: () => void,
  more: Partial<Pick<Item, "tone" | "shortcut" | "checked" | "disabled">> = {},
): Item => ({ id, label, onSelect, ...more });
const danger = { tone: "danger" } as const;
const line = (id: string): MenuItem => ({ type: "separator", id });
const submenu = (id: string, label: string, items: MenuItem[]): MenuItem => ({
  type: "submenu",
  id,
  label,
  items,
});

/** Drops separators at the ends and next to each other, so a menu never shows an empty group. */
function tidy(items: (MenuItem | false | null | undefined | "")[]): MenuItem[] {
  const out: MenuItem[] = [];
  for (const i of items) {
    if (!i) continue;
    if (i.type === "separator" && (!out.length || out[out.length - 1]?.type === "separator")) continue;
    out.push(i);
  }
  while (out[out.length - 1]?.type === "separator") out.pop();
  return out;
}

/** What notifications are set to, for a chat, a channel or a space. */
type Notify = { muted_until: string | null; mentions_only: boolean };

/** The keys the chat list rows answer to, shown beside their items. */
export const ROW_KEYS = {
  unread: "Alt+U",
  pin: "Alt+P",
  star: "Alt+S",
  archive: "Alt+A",
  info: "Mod+I",
} as const;

/**
 * The actions for every kind of item, in one place, so a chat offers the same choices in the chat
 * list, its header and its info page, on every device.
 */
export function useMenus() {
  const api = useApi();
  const client = useClient();
  const me = useMe();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const queravelPanel = useQueravelPanel();
  const { platform } = useSession();
  const feedback = useFeedback();
  const origin = platform.serverOrigin() || window.location.origin;

  const fail = (e: unknown) => feedback.notify((e as Error)?.message || "That did not work. Try again.");
  /** Runs an action, then says it is done, with Undo when it can be taken back. */
  const run = (job: () => Promise<unknown>, done?: string, undo?: () => Promise<unknown>) =>
    void job()
      .then(() => done && feedback.notify(done, undo ? () => void undo().catch(fail) : undefined))
      .catch(fail);
  const ask = (title: string, detail: string, action: string, job: () => Promise<unknown>, done?: string) =>
    void feedback.confirm({ title, detail, action }).then((yes) => yes && run(job, done));
  const copy = (text: string, done: string) =>
    void copyText(text, done).then((note) => feedback.notify(note));
  /** Your settings for a chat. They show at once and go back if the server says no. */
  const settings = (chat: Chat, body: Parameters<typeof api.updateChatSettings>[1]) =>
    client.setChatSettings(chat.id, body);
  const chatInfo = (c: Chat, add = false) =>
    navigate(
      desktop
        ? `/chats/${c.id}?panel=info${add ? "&add=1" : ""}`
        : `/chats/${c.id}/info${add ? "?add=1" : ""}`,
    );
  const channelInfo = (c: Chat, spaceId: string, add = false) =>
    navigate(
      `${spaceHref(spaceId, c.id)}${desktop ? "?panel=info" : "/info"}${add ? `${desktop ? "&" : "?"}add=1` : ""}`,
    );

  // Read state, pinning, starring and archiving, which chats and channels share.

  const readItem = (c: Chat): Item =>
    isUnread(c) || c.mentioned
      ? item(
          "read",
          "Mark as read",
          () =>
            run(async () => {
              if (c.marked_unread) await client.markUnread(c.id, false);
              await api.markRead(c.id, c.last_seq);
              await client.refreshChat(c.id);
            }),
          { shortcut: ROW_KEYS.unread },
        )
      : item("unread", "Mark as unread", () => run(() => client.markUnread(c.id, true)), {
          shortcut: ROW_KEYS.unread,
        });

  const pinItem = (c: Chat): Item =>
    c.pinned
      ? item(
          "unpin",
          "Unpin",
          () =>
            run(
              () => settings(c, { pinned: false }),
              "Unpinned.",
              () => settings(c, { pinned: true }),
            ),
          { shortcut: ROW_KEYS.pin },
        )
      : item(
          "pin",
          "Pin to top",
          () =>
            run(
              () => settings(c, { pinned: true }),
              "Pinned to the top.",
              () => settings(c, { pinned: false }),
            ),
          { shortcut: ROW_KEYS.pin },
        );

  const starItem = (c: Chat): Item =>
    c.starred
      ? item(
          "unstar",
          "Unstar",
          () =>
            run(
              () => client.starChat(c.id, false),
              "Removed from Starred.",
              () => client.starChat(c.id, true),
            ),
          { shortcut: ROW_KEYS.star },
        )
      : item(
          "star",
          "Star",
          () =>
            run(
              () => client.starChat(c.id, true),
              c.kind === "channel" ? "Starred. It shows at the top of the space." : "Added to Starred.",
              () => client.starChat(c.id, false),
            ),
          { shortcut: ROW_KEYS.star },
        );

  const archiveItem = (c: Chat): Item =>
    c.archived
      ? item(
          "unarchive",
          "Unarchive",
          () =>
            run(
              () => settings(c, { archived: false }),
              "Moved back to your chats.",
              () => settings(c, { archived: true }),
            ),
          { shortcut: ROW_KEYS.archive },
        )
      : item(
          "archive",
          "Archive",
          () =>
            run(
              () => settings(c, { archived: true }),
              "Archived.",
              () => settings(c, { archived: false }),
            ),
          { shortcut: ROW_KEYS.archive },
        );

  /** Mute for a while or until turned back on. Undo puts back what it was. */
  const muteChoices = (
    current: Notify,
    apply: (next: Partial<Notify>) => Promise<unknown>,
    label: (choice: MuteChoice) => string,
  ): MenuItem[] =>
    MUTE_CHOICES.map((choice) =>
      item(
        `mute-${choice.id}`,
        label(choice),
        () => {
          const until = muteEnd(choice);
          run(
            () => apply({ muted_until: until }),
            mutedNote(until),
            () => apply({ muted_until: current.muted_until }),
          );
        },
        choice.id === "always" && isMutedUntil(current.muted_until)
          ? { checked: mutedForever(current.muted_until) }
          : {},
      ),
    );

  /** A direct chat's Mute, which opens how long for, or Unmute. */
  const muteItem = (c: Chat): MenuItem => {
    const current = { muted_until: c.muted_until, mentions_only: c.mentions_only };
    if (isMutedUntil(c.muted_until))
      return item("unmute", "Unmute", () =>
        run(
          () => settings(c, { muted_until: null }),
          "Unmuted.",
          () => settings(c, { muted_until: current.muted_until }),
        ),
      );
    return submenu(
      "mute",
      "Mute",
      muteChoices(
        current,
        (next) => settings(c, next),
        (choice) => choice.label,
      ),
    );
  };

  /**
   * Notifications for a group, a channel or a space: every message, mentions only, or muted for a
   * while. `follow` adds following the space's setting, for a channel.
   */
  const notifyMenu = (
    current: Notify,
    apply: (next: Partial<Notify>) => Promise<unknown>,
    restore: () => Promise<unknown>,
    follow?: { on: boolean; space: Notify | null; apply: () => Promise<unknown> },
  ): MenuItem => {
    const muted = isMutedUntil(current.muted_until);
    const own = !follow?.on;
    const choose = (next: Partial<Notify>, done: string) => () => run(() => apply(next), done, restore);
    const spaceWords = follow?.space
      ? isMutedUntil(follow.space.muted_until)
        ? "Muted"
        : follow.space.mentions_only
          ? "Mentions only"
          : "All messages"
      : "";
    return submenu(
      "notify",
      "Notifications",
      [
        ...(follow
          ? [
              item(
                "follow",
                spaceWords ? `Follow space setting · ${spaceWords}` : "Follow space setting",
                () => run(follow.apply, "Following your setting for the space.", restore),
                { checked: follow.on },
              ),
              line("n0"),
            ]
          : []),
        item(
          "all",
          "All messages",
          choose({ muted_until: null, mentions_only: false }, "You hear about every message."),
          { checked: own && !muted && !current.mentions_only },
        ),
        item(
          "mentions",
          "Mentions only",
          choose({ muted_until: null, mentions_only: true }, "You hear only when someone mentions you."),
          { checked: own && !muted && current.mentions_only },
        ),
        line("n1"),
        // A mute for a while shows when it ends, ticked, above the other choices.
        muted &&
          own &&
          !mutedForever(current.muted_until) &&
          item("muted", `Muted until ${muteEndLabel(current.muted_until)}`, () => undefined, {
            checked: true,
            disabled: "Choose Unmute, or another time.",
          }),
        ...muteChoices(current, apply, (choice) =>
          choice.id === "always" ? "Mute until I turn it back on" : `Mute ${choice.label.toLowerCase()}`,
        ).map((m) =>
          m.type !== "separator" && m.type !== "submenu" && m.checked !== undefined && !own
            ? { ...m, checked: false }
            : m,
        ),
        muted &&
          own &&
          item("unmute", "Unmute", () => run(() => apply({ muted_until: null }), "Unmuted.", restore)),
      ].filter((x): x is MenuItem => Boolean(x)),
    );
  };

  const callItems = (c: Chat): MenuItem[] => [
    item("voice", "Voice call", () => navigate(`/calls/${c.id}?media=voice`)),
    item("video", "Video call", () => navigate(`/calls/${c.id}?media=video`)),
  ];

  const clear = (chat: Chat, title: string): Item =>
    item(
      "clear",
      "Clear messages…",
      () =>
        ask(
          `Clear messages in ${title}?`,
          "They are removed from all your devices. Others in the chat keep theirs.",
          "Clear messages",
          () => client.clearChat(chat.id),
          "Messages cleared.",
        ),
      danger,
    );

  /** Block or unblock someone. Lists showing them update at once. */
  const blockItem = (user: PublicUser, after?: () => unknown): Item => {
    const name = firstName(user.display_name);
    const done = async () => {
      contactsChanged();
      await after?.();
    };
    return user.is_blocked
      ? item(`unblock`, `Unblock ${name}`, () =>
          run(async () => {
            await api.unblock(user.id);
            await done();
          }, `${name} is unblocked.`),
        )
      : item(
          "block",
          `Block ${name}…`,
          () =>
            ask(
              `Block ${user.display_name}?`,
              "They cannot message or call you. They are not told.",
              "Block",
              async () => {
                await api.block(user.id);
                await done();
              },
              `${name} is blocked.`,
            ),
          danger,
        );
  };

  /** A chat with a person, a group, or your own chat. */
  const chat = (c: Chat): MenuItem[] => {
    const title = chatTitle(c, me.id);
    if (c.kind === "self")
      return tidy([readItem(c), pinItem(c), starItem(c), line("s1"), clear(c, "your own chat")]);
    if (c.kind === "group") {
      const current = { muted_until: c.muted_until, mentions_only: c.mentions_only };
      return tidy([
        readItem(c),
        pinItem(c),
        starItem(c),
        notifyMenu(
          current,
          (next) => settings(c, next),
          () => settings(c, current),
        ),
        archiveItem(c),
        line("s1"),
        ...callItems(c),
        c.can_add && item("add", "Add people", () => chatInfo(c, true)),
        c.invite_code &&
          item("invite", "Copy invite link", () =>
            copy(`${origin}/join/${c.invite_code}`, "Invite link copied."),
          ),
        item("info", "Group info", () => chatInfo(c), { shortcut: ROW_KEYS.info }),
        line("s2"),
        clear(c, title),
        item(
          "leave",
          "Leave group…",
          () => {
            const words = leaveGroupAsk(title);
            ask(words.title, words.detail, words.action, async () => {
              // Leaving starts new keys, so this person cannot read what the group says next.
              await client.leaveGroup(c.id);
              navigate("/chats");
            });
          },
          danger,
        ),
        item("report", "Report…", () => feedback.report({ chatId: c.id, name: title }), danger),
      ]);
    }
    const peer = peerOf(c, me.id);
    return tidy([
      readItem(c),
      pinItem(c),
      starItem(c),
      muteItem(c),
      archiveItem(c),
      line("s1"),
      ...callItems(c),
      item("info", "Contact info", () => chatInfo(c), { shortcut: ROW_KEYS.info }),
      line("s2"),
      clear(c, title),
      item(
        "delete",
        "Delete chat…",
        () =>
          ask(
            `Delete your chat with ${title}?`,
            `It is removed from your list and from all your devices. ${firstName(title)} keeps their copy. A new message brings the chat back.`,
            "Delete chat",
            async () => {
              await client.clearChat(c.id, true);
              navigate("/chats");
            },
          ),
        danger,
      ),
      peer && blockItem(peer, () => client.refreshChat(c.id)),
      peer &&
        item(
          "report",
          "Report…",
          () => feedback.report({ userId: peer.id, chatId: c.id, name: title }),
          danger,
        ),
    ]);
  };

  /** The Archived row at the top of the chat list. */
  const archived = (chats: Chat[]): MenuItem[] => {
    // The client has the newest copy of your settings, changed here or on another device.
    const keep = (client.getState().me ?? me).keep_archived === true;
    const unread = chats.some((c) => isUnread(c) || c.mentioned);
    return tidy([
      item(
        "read",
        "Mark all as read",
        () => run(() => client.markArchivedRead(), "Every archived chat is read."),
        unread ? {} : { disabled: "Nothing in Archived is unread." },
      ),
      item(
        "keep",
        "Keep archived when new messages arrive",
        () =>
          run(
            () => client.setKeepArchived(!keep),
            keep
              ? "A new message brings an archived chat back to your list."
              : "Archived chats stay archived when new messages arrive.",
            () => client.setKeepArchived(keep),
          ),
        { checked: keep },
      ),
    ]);
  };

  /** Your Queravel chat in the list. */
  const queravel = (assistant: Chat | undefined): MenuItem[] =>
    tidy([
      item("new", "New conversation", () => queravelPanel.show({ fresh: true })),
      assistant &&
        item(
          "delete-all",
          "Delete all Queravel conversations…",
          () =>
            ask(
              "Delete all your Queravel conversations?",
              "Every question and answer is removed from all your devices. This cannot be undone.",
              "Delete all",
              () => client.clearChat(assistant.id),
            ),
          danger,
        ),
    ]);

  /**
   * One Queravel conversation in the history: its questions and answers share an id. `ids` are
   * all its stored messages, titles included.
   */
  const queravelConversation = (
    assistantId: string,
    conversation: { id: string | null; ids: string[]; name: string; title: string | null },
  ): MenuItem[] => {
    const { id, ids, name, title } = conversation;
    const rename = async () => {
      const next = await feedback.name({
        title: "Rename conversation",
        label: "Name",
        value: title ?? name,
        placeholder: name,
        action: "Rename",
      });
      if (next === null || !id || next === (title ?? name) || (!next && !title)) return;
      run(
        () => client.send(assistantId, { type: "queravel_title", conversation: id, title: next }),
        "Renamed.",
      );
    };
    return tidy([
      !!id && item("rename", "Rename", () => void rename()),
      line("q1"),
      item(
        "delete",
        "Delete conversation…",
        () =>
          ask(
            "Delete this conversation?",
            "Its questions and answers are removed from all your devices. This cannot be undone.",
            "Delete conversation",
            async () => {
              await client.deleteMessages(assistantId, ids);
              // The Queravel panel showing it starts a new conversation.
              if (id && queravelPanel.conversation === id) queravelPanel.startNew();
            },
            "Conversation deleted.",
          ),
        danger,
      ),
    ]);
  };

  /** One Queravel answer: copy it, or put it in the message box of the chat it is about. */
  const queravelAnswer = (answer: string, onReply?: () => void): MenuItem[] =>
    tidy([
      item("copy", "Copy answer", () => copy(answer, "Answer copied.")),
      onReply && item("reply", "Use as reply", onReply),
    ]);

  /**
   * A channel in a space. `space` is the space it is listed in, which for a shared channel may not
   * be the one that owns it. Only the owning space's admins manage it. The other space's admins can
   * take it out of their space.
   */
  /** A channel's Notifications, which can follow your setting for the space. */
  const channelNotify = (c: Chat, space: Space | undefined): MenuItem => {
    const current = { muted_until: c.muted_until, mentions_only: c.mentions_only };
    const follows = c.notify_follows_space === true;
    // Back to what it was: following the space, or the channel's own setting.
    const restore = () => settings(c, follows ? { follow_space: true } : current);
    return notifyMenu(current, (next) => settings(c, next), restore, {
      on: follows,
      space: space
        ? { muted_until: space.muted_until ?? null, mentions_only: space.mentions_only ?? true }
        : null,
      apply: () => settings(c, { follow_space: true }),
    });
  };

  /** Leaving a channel asks first, from its menu or its info page. New keys follow. */
  const leaveChannel = (c: Chat, space: Space | undefined) => {
    const spaceId = space?.id ?? c.space_id ?? "";
    ask(
      `Leave #${c.title}?`,
      isGuest(space)
        ? "You are a guest, so only an admin can add you back."
        : c.private
          ? "It is private, so someone in it has to add you back."
          : "You can join again from Browse channels.",
      "Leave channel",
      async () => {
        await client.leaveChannel(spaceId, c.id);
        navigate(spaceHref(spaceId));
      },
    );
  };

  const channel = (c: Chat, space: Space | undefined): MenuItem[] => {
    const spaceId = space?.id ?? c.space_id ?? "";
    const foreign = isForeign(c, spaceId);
    const owningAdmin = isSpaceAdmin(space) && !foreign;
    // The space's last channel cannot be deleted, so the item is left out.
    const owned = channelsOf(client.getState(), spaceId).filter((x) => !isForeign(x, spaceId));
    return tidy([
      readItem(c),
      starItem(c),
      channelNotify(c, space),
      line("s1"),
      item("info", "Channel info", () => channelInfo(c, spaceId), { shortcut: ROW_KEYS.info }),
      c.can_add && item("add", "Add people", () => channelInfo(c, spaceId, true)),
      item("copy", "Copy link", () =>
        copy(`${origin}${spaceHref(spaceId, c.id)}`, "Link copied. Only people in the space can open it."),
      ),
      owningAdmin &&
        item("share", "Share with another space", () => navigate(`/spaces/${spaceId}/manage?share=${c.id}`)),
      line("s2"),
      item("leave", "Leave channel…", () => leaveChannel(c, space), danger),
      foreign &&
        isSpaceAdmin(space) &&
        space &&
        item(
          "unshare",
          "Remove from this space…",
          () =>
            ask(
              `Remove #${c.title} from ${space.name}?`,
              `People in ${space.name} leave it unless they are also in ${c.space_name || "the space that owns it"}. Only that space can share it again.`,
              "Remove",
              async () => {
                await client.stopSharing(spaceId, c.id, spaceId);
                navigate(spaceHref(spaceId));
              },
            ),
          danger,
        ),
      owningAdmin &&
        owned.length > 1 &&
        item(
          "delete",
          "Delete channel…",
          () =>
            ask(
              `Delete #${c.title}?`,
              "Every message and file in it is deleted for everyone. This cannot be undone.",
              "Delete channel",
              async () => {
                await client.deleteChannel(spaceId, c.id);
                navigate(spaceHref(spaceId));
              },
            ),
          danger,
        ),
    ]);
  };

  /** A space, from its rail tile, the Spaces list and the … beside its name. */
  const space = (s: Space): MenuItem[] => {
    const admin = isSpaceAdmin(s);
    const { unread } = spaceActivity(client.getState(), s.id);
    const anyUnread = unread || channelsOf(client.getState(), s.id).some((c) => isUnread(c) || c.mentioned);
    const current: Notify = { muted_until: s.muted_until ?? null, mentions_only: s.mentions_only ?? true };
    const apply = (next: Partial<Notify>) => client.setSpaceNotifications(s.id, next);
    const alone = s.member_count <= 1;
    return tidy([
      anyUnread &&
        item("read", "Mark all as read", () =>
          run(() => client.markSpaceRead(s.id), "Everything in the space is read."),
        ),
      notifyMenu(current, apply, () => apply(current)),
      line("s0"),
      admin && item("invite", "Invite people", () => navigate(`${spaceHref(s.id)}?invite=1`)),
      admin && item("guest", "Invite a guest", () => navigate(`${spaceHref(s.id)}?guest=1`)),
      admin &&
        s.invite_code &&
        item("copy-invite", "Copy invite link", () =>
          copy(spaceInviteLink(s.invite_code as string, origin), "Invite link copied."),
        ),
      line("s1"),
      item("settings", "Space settings", () => navigate(`${spaceHref(s.id)}?settings=1`)),
      admin && item("manage", "Manage space", () => navigate(`/spaces/${s.id}/manage`)),
      item("members", "Members", () => navigate(`/spaces/${s.id}/members`)),
      line("s2"),
      item(
        "leave",
        "Leave space…",
        () =>
          ask(
            `Leave ${s.name}?`,
            s.role === "owner"
              ? "You leave every channel in it. The space passes to its longest-serving admin, or else its longest-serving member."
              : s.role === "guest"
                ? "You leave every channel you were invited to. Only an admin can invite you back."
                : "You leave every channel in it. An invite link brings you back.",
            "Leave space",
            async () => {
              await client.leaveSpace(s.id);
              navigate("/spaces");
            },
          ),
        alone ? { ...danger, disabled: "You are the only member. Delete the space instead." } : danger,
      ),
      s.role === "owner" &&
        item(
          "delete",
          "Delete space…",
          () =>
            ask(
              `Delete ${s.name}?`,
              "Every channel, message and file in it is deleted for everyone. This cannot be undone.",
              "Delete space",
              async () => {
                await client.leaveSpace(s.id, true);
                navigate("/spaces");
              },
            ),
          danger,
        ),
    ]);
  };

  /** A space workflow, on the space's admin page. `onEdit` opens it for editing. */
  const workflow = (w: Workflow, onEdit: () => void): MenuItem[] => {
    const change = (paused: boolean) => () => client.updateWorkflow(w.spaceId, w.id, { paused });
    return tidy([
      item("edit", "Edit", onEdit),
      // One that stopped by itself needs new text before it can run again.
      !w.problem &&
        (w.paused
          ? item("resume", "Resume", () => run(change(false), "Resumed.", change(true)))
          : item("pause", "Pause", () => run(change(true), "Paused.", change(false)))),
      line("s1"),
      item(
        "delete",
        "Delete workflow…",
        () =>
          ask(
            `Delete ${w.name}?`,
            w.kind === "welcome"
              ? "People who join are no longer welcomed. Messages it already posted stay."
              : "It stops posting. Messages it already posted stay.",
            "Delete workflow",
            () => client.deleteWorkflow(w.spaceId, w.id),
          ),
        danger,
      ),
    ]);
  };

  /**
   * Someone in a member list: a space's, a channel's or a group's. `manage` adds what you may do
   * to them there. `onChanged` reloads the list after blocking. Your own row has no menu.
   */
  const person = (
    user: PublicUser,
    manage: {
      /** Their role in the list, which decides the admin choices. */
      role?: SpaceRole;
      /** Where they are, for the words of Remove. */
      where?: "space" | "group" | "channel";
      setRole?: (role: "admin" | "member") => Promise<unknown>;
      remove?: () => Promise<unknown>;
      /** A channel admin taking them out of the channel. */
      removeFromChannel?: { channel: string; run: () => Promise<unknown> };
      onChanged?: () => unknown;
    } = {},
  ): MenuItem[] => {
    if (user.id === me.id) return [];
    const name = user.display_name;
    const direct = async () => client.createDirect(user.id);
    const { role, where = "space", setRole, remove, removeFromChannel, onChanged } = manage;
    const place = where === "group" ? "group" : where === "channel" ? "channel" : "space";
    return tidy([
      item("message", "Message", () => run(async () => navigate(`/chats/${(await direct()).id}`))),
      item("voice", "Voice call", () =>
        run(async () => navigate(`/calls/${(await direct()).id}?media=voice`)),
      ),
      item("video", "Video call", () =>
        run(async () => navigate(`/calls/${(await direct()).id}?media=video`)),
      ),
      item("profile", "Profile", () => navigate(`/contacts/${user.id}`)),
      line("s1"),
      setRole &&
        role === "guest" &&
        item("member", "Make a full member", () =>
          run(() => setRole("member"), `${firstName(name)} is a full member.`),
        ),
      setRole &&
        role !== undefined &&
        role !== "owner" &&
        role !== "guest" &&
        (role === "admin"
          ? item("demote", "Remove as admin", () =>
              run(() => setRole("member"), `${firstName(name)} is no longer an admin.`),
            )
          : item("promote", "Make admin", () =>
              run(() => setRole("admin"), `${firstName(name)} is an admin.`),
            )),
      line("s2"),
      removeFromChannel &&
        item(
          "remove-channel",
          "Remove from channel…",
          () =>
            ask(
              `Remove ${name} from #${removeFromChannel.channel}?`,
              "They stop getting its messages and cannot read what is said after. They stay in the space.",
              "Remove from channel",
              removeFromChannel.run,
              `${firstName(name)} was removed from #${removeFromChannel.channel}.`,
            ),
          danger,
        ),
      remove &&
        role !== "owner" &&
        item(
          "remove",
          `Remove from ${place}…`,
          () =>
            ask(
              `Remove ${name}?`,
              place === "group"
                ? "They leave the group and stop getting its messages. An admin can add them back."
                : role === "guest"
                  ? "They leave every channel they were invited to."
                  : "They leave every channel in the space. They can come back with an invite link.",
              `Remove from ${place}`,
              remove,
              `${firstName(name)} was removed.`,
            ),
          danger,
        ),
      blockItem(user, onChanged),
      item("report", "Report…", () => feedback.report({ userId: user.id, name }), danger),
    ]);
  };

  return {
    chat,
    archived,
    queravel,
    queravelConversation,
    queravelAnswer,
    channel,
    channelNotify,
    leaveChannel,
    space,
    workflow,
    person,
  };
}
